import { c as _c } from "react/compiler-runtime";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { toast } from "sonner";
import { api, type CategoryFacet, type Product, type Storefront } from "@/api";
import { EmptyState, ErrorState } from "@/components/common/states";
import { ChatIcon, ChevronLeftIcon, HeartFilledIcon, HeartLine16Icon, InfoCircleIcon, Share16Icon, StarIcon } from "@/components/icons";
import { ProductGridSkeleton } from "@/components/product/product-grid";
import { QuickViewModal } from "@/components/product/quick-view";
import { Button } from "@/components/ui/button";
import { AboutBlocks, AboutBrandView, BannerSlider, ColumnsGrid, countFilters, DeliveryTab, InteriorPinsView, NewsBlock, OutfitGalleryView, SeasonalSaleBlock, Section, ShopFilterBar, ShopNavTabs, type ShopTabKey, type SortKey, type StoreFilters, toProductQuery, useNarrow } from "@/components/shop/boutique";
import { CustomOrderTab } from "@/components/shop/custom-order";
import { ShopInfoModal } from "@/components/shop/shop-info-modal";
import { VerifiedSellerBadge } from "@/components/shop/verified-badge";
import { ReviewsModal } from "@/components/shop/reviews-modal";
import { LeaveReviewModal } from "@/components/shop/leave-review-modal";
import { Skeleton } from "@osmix/ui/skeleton";
import { DiscoverySourceProvider } from "@/lib/discovery-source";
import { useCloseOnBack, useGoBack } from "@/lib/history-back";
import { cn } from "@/lib/utils";
import { useWishlist } from "@/store";
import { usePageMeta } from "@/lib/use-page-meta";

// fd-boutique-10: порядок блоков «Главной» витрины. Ключи совпадают с
// BlockOrderEditor конструктора (seller-web), поэтому расстановка продавца
// повторяется здесь один в один. `config.blockOrder` задаёт позиции названных
// ключей; не названные остаются в порядке по умолчанию (в конце). По умолчанию
// лента «Все товары» идёт сразу под баннером, как в кадре Figma, подборки —
// ниже.
export const DEFAULT_BLOCK_ORDER = ["banner", "products", "featured", "collections", "sale", "news", "interactive", "displayBanner", "partners", "aboutBrand", "about"] as const;
type LandingBlockKey = (typeof DEFAULT_BLOCK_ORDER)[number];
export function orderLandingBlocks(blockOrder?: string[]): LandingBlockKey[] {
  const valid = new Set<string>(DEFAULT_BLOCK_ORDER);
  const saved = (blockOrder ?? []).filter((k): k is LandingBlockKey => valid.has(k));
  const rest = DEFAULT_BLOCK_ORDER.filter(k => !saved.includes(k));
  return [...new Set([...saved, ...rest])];
}

/** Листовые категории магазина из фасета (дерево со счётчиками). */
function leafCategories(nodes: CategoryFacet[]): {
  id: string;
  label: string;
}[] {
  const out: {
    id: string;
    label: string;
  }[] = [];
  const walk = (list: CategoryFacet[]) => {
    for (const n of list) {
      const kids = (n.children ?? []).filter(k => k.count > 0);
      if (kids.length) walk(kids);else if (n.count > 0) out.push({
        id: n.value,
        label: n.label
      });
    }
  };
  walk(nodes);
  return out;
}
const PAGE_SIZE = 24;
export function ShopPage() {
  const $ = _c(7);
  const {
    slug: t0
  } = useParams();
  const slug = t0 === undefined ? "" : t0;
  usePageMeta();
  let t1;
  if ($[0] !== slug) {
    t1 = {
      queryKey: ["shops", "storefront", slug],
      queryFn: () => api.shops.storefront(slug)
    };
    $[0] = slug;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const store = useQuery(t1);
  if (store.isPending) {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="flex flex-col gap-8"><Skeleton className="h-11 w-40 rounded-full" /><Skeleton className="aspect-[21/8] w-full rounded-none md:aspect-[15/4]" /><ProductGridSkeleton /></div>;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  if (store.error || !store.data) {
    let t2;
    if ($[3] !== store) {
      t2 = <ErrorState onRetry={() => store.refetch()} />;
      $[3] = store;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t2;
  if ($[5] !== store.data) {
    t2 = <DiscoverySourceProvider value="catalog"><ShopView key={store.data.shop.id} store={store.data} /></DiscoverySourceProvider>;
    $[5] = store.data;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function ShopView({
  store
}: {
  store: Storefront;
}) {
  const {
    t
  } = useTranslation();
  const navigate = useNavigate();
  // Витрину часто открывают по прямой ссылке (ссылка магазина /s/<код>,
  // мессенджер): истории на сайте нет, и `navigate(-1)` уводил с OSMIX или не
  // делал ничего. Тогда «Назад» ведёт на главную.
  const goBack = useGoBack("/");
  const {
    shop,
    config
  } = store;
  const savedShop = useWishlist(s => s.shopIds.includes(shop.id));
  const toggleShop = useWishlist(s_0 => s_0.toggleShop);
  const setShopLikedOnServer = useWishlist(s_1 => s_1.setShopLikedOnServer);
  const narrow = useNarrow();

  // Модалки страницы магазина (Figma): «Информация», «Отзывы», «Оставить отзыв».
  const [infoOpen, setInfoOpen] = useState(false);
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const [leaveReviewOpen, setLeaveReviewOpen] = useState(false);
  const [quickViewId, setQuickViewId] = useState<string | null>(null);
  // Системный «назад» на телефоне закрывает открытую модалку, а не уводит со
  // страницы из-под неё.
  useCloseOnBack(infoOpen || reviewsOpen || leaveReviewOpen, () => {
    setInfoOpen(false);
    setReviewsOpen(false);
    setLeaveReviewOpen(false);
  });
  const customEnabled = config.header.customOrdersEnabled;
  // Вкладка из ссылки: ?tab=custom|about|ideas (кнопка «Индивидуальный заказ»
  // в письме/чате ведёт сразу на мастер), ?category=<название> — ссылка «Все
  // <категория> <магазин>» с карточки товара.
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTabState] = useState<ShopTabKey>(() => {
    const raw = searchParams.get("tab");
    if (raw === "about" || raw === "ideas" || raw === "custom" && customEnabled) return raw;
    return "main";
  });
  const setTab = (next: ShopTabKey) => {
    setTabState(next);
    const params = new URLSearchParams(searchParams);
    params.delete("category");
    if (next === "custom" || next === "about" || next === "ideas") params.set("tab", next);else params.delete("tab");
    setSearchParams(params, {
      replace: true
    });
  };
  const [sort, setSort] = useState<SortKey>("popular");
  const [filters, setFilters] = useState<StoreFilters>({});
  const [columns, setColumns] = useState(4);
  const [mobileColumns, setMobileColumns] = useState(2);
  const onColumnsChange = (n: number) => narrow ? setMobileColumns(n) : setColumns(n);

  // Оси фильтров магазина — из фасетов шлюза (все товары, а не первая страница).
  const facets = useQuery({
    queryKey: ["products", "facets", "shop", shop.id],
    queryFn: () => api.products.facets({
      shopId: shop.id
    })
  });
  const categories = useMemo(() => leafCategories(facets.data?.categories ?? []), [facets.data]);
  const colors = (facets.data?.colors ?? []).map(c => c.value);
  const sizes = (facets.data?.sizes ?? []).map(s_2 => s_2.value);
  const priceBounds = facets.data?.price ?? {
    min: 0,
    max: 0
  };
  const collectionOptions = config.collections.items.map(c_0 => ({
    id: c_0.id,
    title: c_0.title
  }));

  // ?category=<название> → вкладка категории, как только известны категории.
  useEffect(() => {
    const name = searchParams.get("category");
    if (!name || categories.length === 0) return;
    const match = categories.find(c_1 => c_1.label === name);
    if (match) setTabState(`cat:${match.id}`);
  }, [categories, searchParams]);
  const categoryTabId = tab.startsWith("cat:") ? tab.slice(4) : undefined;

  // Товары подборок (закреплённые, партнёрские, интерактивные блоки) — по id.
  const curatedIds = useMemo(() => {
    const ids = new Set<string>([...config.pinnedProducts, ...config.partnerProducts]);
    for (const b of config.interactiveBlocks) {
      if (b.type === "interior-pins") for (const s_3 of b.slides) for (const p of s_3.pins) ids.add(p.productId);
      if (b.type === "outfit-gallery") for (const o of b.outfits) for (const id of o.productIds) ids.add(id);
    }
    return [...ids];
  }, [config]);
  const curated = useQuery({
    queryKey: ["products", "byIds", "shop-curated", shop.id, curatedIds],
    queryFn: () => api.products.byIds(curatedIds),
    enabled: curatedIds.length > 0
  });
  const byId = useMemo(() => new Map((curated.data ?? []).map(p_0 => [p_0.id, p_0])), [curated.data]);
  const resolve = (ids_0: string[]): Product[] => ids_0.map(id_0 => byId.get(id_0)).filter((p_1): p_1 is Product => Boolean(p_1));
  const featured = resolve(config.pinnedProducts);
  const partners = resolve(config.partnerProducts);
  // fd-boutique-12: резолвнутые товары партнёров, сгруппированы по магазину.
  const partnerGroups = useMemo(() => {
    const map = new Map<string, {
      shopId: string;
      shopName: string;
      products: Product[];
    }>();
    for (const entry of store.partnerCatalog ?? []) {
      const group = map.get(entry.partnerShopId) ?? {
        shopId: entry.partnerShopId,
        shopName: entry.partnerShopName ?? "",
        products: []
      };
      group.products.push(entry.product);
      map.set(entry.partnerShopId, group);
    }
    return [...map.values()];
  }, [store.partnerCatalog]);
  const interactive = config.interactiveBlocks;
  const interiorPins = interactive.filter(b_0 => b_0.type === "interior-pins");
  const outfitGalleries = interactive.filter(b_1 => b_1.type === "outfit-gallery");
  const displayBanner = interactive.find(b_2 => b_2.type === "banner");
  const aboutBrand = interactive.find(b_3 => b_3.type === "about-brand");
  const onToggleShop = () => {
    const liked = toggleShop(shop.id);
    void setShopLikedOnServer(shop.id, liked);
  };

  // fd-boutique-01: «Поделиться» — системное меню, иначе копия ссылки. Ссылка
  // магазина /s/<код>, если продавец её заводил, иначе адрес витрины.
  const share = async () => {
    const url = store.shareCode ? `${window.location.origin}/s/${encodeURIComponent(store.shareCode)}` : `${window.location.origin}/shop/${encodeURIComponent(shop.slug)}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: shop.name,
          text: shop.tagline,
          url
        });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success(t("product.shareCopied"));
      }
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") return;
      toast.error(t("common.error"));
    }
  };
  const writeSeller = () => navigate(`/chats?shop=${encodeURIComponent(shop.slug)}`);

  // Кнопки шапки (Figma: серые M): «В избранное», «Поделиться», «Написать продавцу».
  const actions = <>
			<Button variant="gray" size="md" onClick={onToggleShop} aria-pressed={savedShop}>
				{savedShop ? <HeartFilledIcon size={16} className="text-ds-accent" /> : <HeartLine16Icon className="text-ds-ink-3" />}
				{savedShop ? t("shop.favoriteOn") : t("shop.favorite")}
			</Button>
			<Button variant="gray" size="md" onClick={() => void share()} aria-label={t("shop.share")} data-testid="shop-share" className="max-md:w-10 max-md:px-0">
				<Share16Icon className="text-ds-ink-3" />
				<span className="max-md:hidden">{t("shop.share")}</span>
			</Button>
			<Button variant="gray" size="md" onClick={writeSeller} aria-label={t("shop.writeSeller")} data-testid="shop-write" className="max-md:w-10 max-md:px-0">
				<ChatIcon size={16} className="text-ds-ink-3" />
				<span className="max-md:hidden">{t("shop.writeSeller")}</span>
			</Button>
		</>;
  const bannerOn = config.banner.enabled && (config.banner.slides.some(sl => sl.imageUrl || sl.title) || Boolean(shop.heroUrl));
  const order = orderLandingBlocks(config.blockOrder);
  // Баннер первым — как в кадре: на всю ширину под шапкой магазина, над вкладками.
  const bannerOnTop = bannerOn && order[0] === "banner";
  const anyFilter = countFilters(filters) > 0;
  const shopEmpty = shop.productCount === 0;
  const quickView = (p_2: Product) => setQuickViewId(p_2.id);
  const showAllWith = (patch: StoreFilters) => {
    setTab("main");
    setFilters({
      ...patch
    });
    requestAnimationFrame(() => document.getElementById("shop-products")?.scrollIntoView({
      behavior: "smooth",
      block: "start"
    }));
  };
  const feed = (lockCategory?: string) => <ShopProductsFeed shopId={shop.id} sort={sort} onSortChange={setSort} filters={lockCategory ? {
    ...filters,
    categoryId: lockCategory
  } : filters} onFiltersChange={f => setFilters(lockCategory ? {
    ...f,
    categoryId: undefined
  } : f)} lockCategory={Boolean(lockCategory)} categories={categories} collections={collectionOptions} colors={colors} sizes={sizes} priceBounds={priceBounds} columns={columns} mobileColumns={mobileColumns} onColumnsChange={onColumnsChange} onQuickView={quickView} />;
  const gridColumns = columns;
  const renderBlock = (key: LandingBlockKey) => {
    switch (key) {
      case "banner":
        return bannerOn && !bannerOnTop ? <div key="banner" className="-mx-4 md:-mx-8">
						<BannerSlider slides={config.banner.slides} variant="hero" fallbackImage={shop.heroUrl} />
					</div> : null;
      case "products":
        return <div key="products" id="shop-products" className="scroll-mt-24">
						{feed()}
					</div>;
      case "featured":
        return featured.length > 0 ? <Section key="featured" title={t("shop.featured")}>
						<ColumnsGrid products={featured} columns={gridColumns} mobileColumns={mobileColumns} onQuickView={quickView} />
					</Section> : null;
      case "collections":
        return config.collections.groupByCollection && config.collections.items.length > 0 ? <div key="collections" className="flex flex-col gap-12">
						{config.collections.items.map(c_2 => <CollectionRow key={c_2.id} shopId={shop.id} collection={c_2} columns={gridColumns} mobileColumns={mobileColumns} onQuickView={quickView} onShowAll={() => showAllWith({
            collectionId: c_2.id
          })} />)}
					</div> : null;
      case "sale":
        return config.seasonalSale?.enabled ? <SeasonalSaleBlock key="sale" shopId={shop.id} sale={config.seasonalSale} columns={gridColumns} onQuickView={quickView} onShowAll={() => showAllWith({
          sale: true
        })} /> : null;
      case "news":
        return config.news?.length ? <NewsBlock key="news" news={config.news} limit={3} /> : null;
      case "interactive":
        return interiorPins.length > 0 || outfitGalleries.length > 0 ? <div key="interactive" className="flex flex-col gap-12">
						{interiorPins.map(b_4 => b_4.type === "interior-pins" && <InteriorPinsView key={b_4.id} block={b_4} productById={id_1 => byId.get(id_1)} />)}
						{outfitGalleries.map(b_5 => b_5.type === "outfit-gallery" && <OutfitGalleryView key={b_5.id} block={b_5} productById={id_2 => byId.get(id_2)} />)}
					</div> : null;
      case "displayBanner":
        return displayBanner && displayBanner.type === "banner" ? <BannerSlider key="displayBanner" slides={displayBanner.slides} variant="display" /> : null;
      case "partners":
        if (partners.length === 0 && partnerGroups.length === 0) return null;
        return <Section key="partners" title={t("shop.partner")}>
						<div className="flex flex-col gap-10">
							{partners.length > 0 && <ColumnsGrid products={partners} columns={gridColumns} mobileColumns={mobileColumns} onQuickView={quickView} />}
							{partnerGroups.map(group_0 => <div key={group_0.shopId} className="flex flex-col gap-4">
									{group_0.shopName && <p className="type-body2 text-osmix-text-2">{t("shop.partnerBy", {
                  shop: group_0.shopName
                })}</p>}
									<ColumnsGrid products={group_0.products} columns={gridColumns} mobileColumns={mobileColumns} onQuickView={quickView} />
								</div>)}
						</div>
					</Section>;
      case "aboutBrand":
        return aboutBrand && aboutBrand.type === "about-brand" ? <AboutBrandView key="aboutBrand" block={aboutBrand} /> : null;
      case "about":
        return <AboutBlocks key="about" config={config} shop={shop} photos={featured.flatMap(p_3 => p_3.photos)} />;
      default:
        return null;
    }
  };
  const inspirationEmpty = interiorPins.length === 0 && outfitGalleries.length === 0 && config.collections.items.length === 0 && featured.length === 0;
  return <div className="flex flex-col">
			{/* Figma «Крошки»: «Назад» в 10px под шапкой, шеврон 24 #333 + 14/18 w600 #1F1F1F, зазор 6. */}
			<button type="button" onClick={goBack} className="inline-flex items-center gap-1.5 self-start rounded-[4px] text-[14px] leading-[18px] font-semibold text-[#1F1F1F] outline-none transition-colors hover:text-osmix-text-2 focus-visible:ring-2 focus-visible:ring-ds-focus">
				<ChevronLeftIcon className="text-ds-ink-3" />
				{t("common.back")}
			</button>

			{/* Шапка магазина (fd-boutique-01). Figma: в 36px под «Назад»; лого
       129×129 r16; имя 20/20 w700 #2E2E2E, ⓘ 24; рейтинг 16/22; серые
       кнопки M (h40 r10) — «В избранное», «Поделиться», «Написать
       продавцу» в один ряд (на телефоне переносятся); тариф — бейдж у имени. */}
			<header className="mt-9 flex items-stretch gap-4">
				<div className="size-24 shrink-0 overflow-hidden rounded-[16px] bg-osmix-gray-100 md:size-[129px]">
					{config.header.logoUrl ?? shop.logoUrl ? <img src={config.header.logoUrl ?? shop.logoUrl} alt={shop.name} className="size-full object-cover" /> : <div className="flex size-full items-center justify-center type-h2 text-osmix-icon">{shop.name.slice(0, 1)}</div>}
				</div>
				<div className="flex min-w-0 flex-1 flex-col">
					<div className="flex min-w-0 flex-wrap items-center gap-1.5">
						<h1 className="truncate text-[20px] leading-5 font-bold tracking-[-0.6px] text-[#2E2E2E]">{shop.name}</h1>
						{shop.verified && <VerifiedSellerBadge compact className="md:hidden" />}
						{shop.verified && <VerifiedSellerBadge className="max-md:hidden" />}
						{store.tier === "premium" && <span className="inline-flex h-6 items-center rounded-full bg-ds-primary px-2.5 text-[12px] leading-4 font-semibold text-white" data-testid="shop-tier">
								{t("shop.tierPremium")}
							</span>}
						<button type="button" onClick={() => setInfoOpen(true)} aria-label={t("shop.info.title", {
            defaultValue: "Информация"
          })} className="flex size-6 shrink-0 items-center justify-center rounded-full text-[#333333] outline-none transition-colors hover:text-osmix-text-1 focus-visible:ring-2 focus-visible:ring-ds-focus">
							<InfoCircleIcon />
						</button>
					</div>
					<div className="mt-2.5 flex items-center gap-1.5 text-[16px] leading-[22px] font-medium tracking-[-0.32px] text-[#171A24]/64">
						<span className="flex items-center gap-1">
							<StarIcon size={20} className="text-ds-accent" />
							{shop.rating.toFixed(1)}
						</span>
						<button type="button" onClick={() => setReviewsOpen(true)} className="rounded-[4px] underline underline-offset-2 outline-none hover:text-osmix-tangelo focus-visible:ring-2 focus-visible:ring-ds-focus">
							{t("shop.ratings", {
              count: shop.ratingCount
            })}
						</button>
					</div>
					<div className="mt-auto flex flex-wrap gap-2.5 max-md:hidden">{actions}</div>
				</div>
			</header>
			{/* На телефоне кнопки — строкой под шапкой: «Поделиться» и «Написать» иконками. */}
			<div className="mt-3 flex gap-2.5 md:hidden">{actions}</div>

			<div className={cn("flex flex-col", bannerOnTop ? "mt-4" : "mt-8")}>
				{bannerOnTop && <div className="-mx-4 mb-4 md:-mx-8">
						<BannerSlider slides={config.banner.slides} variant="hero" fallbackImage={shop.heroUrl} />
					</div>}

				<ShopNavTabs categories={categories} active={tab} customEnabled={customEnabled} onChange={setTab} />

				<div className="mt-8">
					{tab === "custom" ? <CustomOrderTab shop={{
          id: shop.id,
          slug: shop.slug,
          name: shop.name
        }} config={config} /> : tab === "about" ? <div className="flex flex-col gap-14" data-testid="shop-about-tab">
							<AboutBlocks config={config} shop={shop} photos={featured.flatMap(p_4 => p_4.photos)} />
							{aboutBrand && aboutBrand.type === "about-brand" ? <AboutBrandView block={aboutBrand} /> : config.about.enabled ? null : <p className="max-w-3xl text-[16px] leading-[22px] whitespace-pre-line text-ds-primary">
									{config.header.description || shop.description}
								</p>}
							{config.news?.length ? <NewsBlock news={config.news} /> : null}
							<DeliveryTab />
						</div> : tab === "ideas" ? inspirationEmpty ? <EmptyState title={t("shop.ideas")} /> : <div className="flex flex-col gap-12">
								{interiorPins.map(b_6 => b_6.type === "interior-pins" && <InteriorPinsView key={b_6.id} block={b_6} productById={id_3 => byId.get(id_3)} />)}
								{outfitGalleries.map(b_7 => b_7.type === "outfit-gallery" && <OutfitGalleryView key={b_7.id} block={b_7} productById={id_4 => byId.get(id_4)} />)}
								{featured.length > 0 && <Section title={t("shop.featured")}>
										<ColumnsGrid products={featured} columns={gridColumns} mobileColumns={mobileColumns} onQuickView={quickView} />
									</Section>}
								{config.collections.items.map(c_3 => <CollectionRow key={c_3.id} shopId={shop.id} collection={c_3} columns={gridColumns} mobileColumns={mobileColumns} onQuickView={quickView} onShowAll={() => showAllWith({
            collectionId: c_3.id
          })} />)}
							</div> : categoryTabId ? feed(categoryTabId) : shopEmpty ? <EmptyState title={t("catalog.empty")} /> : anyFilter ?
        // С фильтрами «Главная» — только лента: подборки ниже не к месту.
        <div id="shop-products" className="scroll-mt-24">
							{feed()}
						</div> : <div className="flex flex-col gap-12" data-testid="shop-landing">
							{order.map(renderBlock)}
						</div>}
				</div>
			</div>

			{/* Модалка «Информация о магазине» (Figma shop-info) */}
			<ShopInfoModal open={infoOpen} onOpenChange={setInfoOpen} shop={shop} categories={categories.map(c_4 => c_4.label)} />

			{/* Модалка «Отзывы» (Figma reviews). */}
			<ReviewsModal open={reviewsOpen} onOpenChange={setReviewsOpen} title={shop.name} summary={{
      average: shop.rating,
      count: shop.ratingCount
    }} items={[]} onLeaveReview={() => {
      setReviewsOpen(false);
      setLeaveReviewOpen(true);
    }} />

			{/* Модалка «Оставить отзыв» (Figma leave-review). */}
			<LeaveReviewModal open={leaveReviewOpen} onOpenChange={setLeaveReviewOpen} subject={{
      photo: config.header.logoUrl ?? shop.logoUrl,
      title: shop.name,
      subtitle: shop.tagline
    }} />

			{/* fd-boutique-04: быстрый просмотр товара без ухода с витрины. */}
			<QuickViewModal productId={quickViewId} open={quickViewId != null} onOpenChange={open => {
      if (!open) setQuickViewId(null);
    }} />
		</div>;
}

// ── Лента товаров магазина (fd-boutique-04/05/06) ────────────────────────────
// Страницы по 24 с «Показать ещё» (раньше — только первые 60 товаров), фильтры
// и сортировка — на шлюзе, сетка 2/4/6 (телефон 1/2), быстрый просмотр.
function ShopProductsFeed(t0) {
  const $ = _c(42);
  const {
    shopId,
    sort,
    onSortChange,
    filters,
    onFiltersChange,
    lockCategory,
    categories,
    collections,
    colors,
    sizes,
    priceBounds,
    columns,
    mobileColumns,
    onColumnsChange,
    onQuickView
  } = t0;
  const {
    t
  } = useTranslation();
  const narrow = useNarrow();
  let t1;
  if ($[0] !== filters || $[1] !== shopId || $[2] !== sort) {
    t1 = toProductQuery(shopId, filters, sort);
    $[0] = filters;
    $[1] = shopId;
    $[2] = sort;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const query = t1;
  let t2;
  if ($[4] !== query || $[5] !== shopId) {
    t2 = ["products", "shop-feed", shopId, query];
    $[4] = query;
    $[5] = shopId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== query) {
    t3 = t4 => {
      const {
        pageParam
      } = t4;
      return api.products.list({
        ...query,
        page: pageParam,
        pageSize: PAGE_SIZE
      });
    };
    $[7] = query;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t2 || $[10] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      initialPageParam: 1,
      getNextPageParam: _temp15
    };
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const page = useInfiniteQuery(t4);
  let t5;
  if ($[12] !== page.data?.pages) {
    const seen = new Set();
    t5 = (page.data?.pages ?? []).flatMap(_temp16).filter(p_0 => seen.has(p_0.id) ? false : (seen.add(p_0.id), true));
    $[12] = page.data?.pages;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const products = t5;
  const total = page.data?.pages[0]?.meta.total ?? 0;
  const left = Math.max(0, total - products.length);
  const t6 = narrow ? mobileColumns : columns;
  let t7;
  if ($[14] !== categories || $[15] !== collections || $[16] !== colors || $[17] !== filters || $[18] !== lockCategory || $[19] !== onColumnsChange || $[20] !== onFiltersChange || $[21] !== onSortChange || $[22] !== priceBounds || $[23] !== sizes || $[24] !== sort || $[25] !== t6) {
    t7 = <ShopFilterBar sort={sort} onSortChange={onSortChange} filters={filters} onFiltersChange={onFiltersChange} categories={categories} collections={collections} colors={colors} sizes={sizes} priceBounds={priceBounds} columns={t6} onColumnsChange={onColumnsChange} lockCategory={lockCategory} />;
    $[14] = categories;
    $[15] = collections;
    $[16] = colors;
    $[17] = filters;
    $[18] = lockCategory;
    $[19] = onColumnsChange;
    $[20] = onFiltersChange;
    $[21] = onSortChange;
    $[22] = priceBounds;
    $[23] = sizes;
    $[24] = sort;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== columns || $[28] !== mobileColumns || $[29] !== onQuickView || $[30] !== page || $[31] !== products || $[32] !== t) {
    t8 = <div className="mt-[26px]">{page.isPending ? <ProductGridSkeleton /> : page.error ? <ErrorState onRetry={() => page.refetch()} /> : products.length === 0 ? <EmptyState title={t("shop.emptyFiltered")} /> : <ColumnsGrid products={products} columns={columns} mobileColumns={mobileColumns} onQuickView={onQuickView} />}</div>;
    $[27] = columns;
    $[28] = mobileColumns;
    $[29] = onQuickView;
    $[30] = page;
    $[31] = products;
    $[32] = t;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  let t9;
  if ($[34] !== left || $[35] !== page || $[36] !== t) {
    t9 = page.hasNextPage && <Button variant="gray" size="lg" className="mt-8 self-center" disabled={page.isFetchingNextPage} onClick={() => void page.fetchNextPage()} data-testid="shop-show-more">{page.isFetchingNextPage ? t("common.loading") : t("shop.showMoreCount", {
        count: Math.min(left, PAGE_SIZE)
      })}</Button>;
    $[34] = left;
    $[35] = page;
    $[36] = t;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  let t10;
  if ($[38] !== t7 || $[39] !== t8 || $[40] !== t9) {
    t10 = <div className="flex flex-col">{t7}{t8}{t9}</div>;
    $[38] = t7;
    $[39] = t8;
    $[40] = t9;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  return t10;
}

// ── Коллекция бутика (fd-boutique-09/11) ─────────────────────────────────────
// Состав — на шлюзе (явные товары + товары её категорий), здесь первые два
// ряда и «Смотреть все» — лента с фильтром по коллекции.
function _temp16(p) {
  return p.data;
}
function _temp15(last) {
  return last.meta.page < last.meta.totalPages ? last.meta.page + 1 : undefined;
}
function CollectionRow(t0) {
  const $ = _c(19);
  const {
    shopId,
    collection,
    columns,
    mobileColumns,
    onQuickView,
    onShowAll
  } = t0;
  const {
    t
  } = useTranslation();
  const limit = Math.max(4, columns) * 2;
  let t1;
  if ($[0] !== collection.id || $[1] !== limit || $[2] !== shopId) {
    t1 = {
      queryKey: ["products", "list", shopId, "collection", collection.id, limit],
      queryFn: () => api.products.list({
        shopId,
        collectionId: collection.id,
        pageSize: limit
      })
    };
    $[0] = collection.id;
    $[1] = limit;
    $[2] = shopId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const list = useQuery(t1);
  let t2;
  if ($[4] !== list.data?.data) {
    t2 = list.data?.data ?? [];
    $[4] = list.data?.data;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const items = t2;
  if (items.length === 0) {
    return null;
  }
  const more = (list.data?.meta.total ?? 0) > items.length;
  let t3;
  if ($[6] !== more || $[7] !== onShowAll || $[8] !== t) {
    t3 = more ? <Button variant="text" onClick={onShowAll}>{t("common.viewAll")}</Button> : undefined;
    $[6] = more;
    $[7] = onShowAll;
    $[8] = t;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== columns || $[11] !== items || $[12] !== mobileColumns || $[13] !== onQuickView) {
    t4 = <ColumnsGrid products={items} columns={columns} mobileColumns={mobileColumns} onQuickView={onQuickView} />;
    $[10] = columns;
    $[11] = items;
    $[12] = mobileColumns;
    $[13] = onQuickView;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== collection.title || $[16] !== t3 || $[17] !== t4) {
    t5 = <Section title={collection.title} action={t3}>{t4}</Section>;
    $[15] = collection.title;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
}